import React from "react";
import ReactDOMServer from "react-dom/server";

import { faAngleLeft, faAngleRight, faArrowToBottom, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PhotoSwipe from "photoswipe";

import PhotoSwipeUI, { type Options as PSUIOptions } from "./lightbox-photoswipe-theme";

type LightboxProps = PSUIOptions & {
	items: PhotoSwipe.Item[];
	initialIndex: number;
	onDestroy?: () => void;
	afterChange?: (pswp: PhotoSwipe<PSUIOptions>) => void;
	photoSwipeUIInnerHTML?: string;
	// In most cases should ignore "photoSwipeUIInnerHTML" and just use the default template, since it is
	// coupling with some of the hooks or event handlers (e.g. 'addCaptionHTMLFn' uses the "pswp_caption" class name
	// to search for and update the caption). If passed in, ii's the caller's responsibility to handle those differences.
};

/**
 * The HTML markup we need for PhotoSwipe and our PhotoSwipe UI implementation to work. So far, mostly unchanged from
 * the original, apart from deleting elements we don't use.
 *
 * ## `.pswp__bg`
 * The background: it's a separate element as animating opacity is far faster than background-color.
 *
 * ## `.pswp__scroll-wrap`
 * Slides wrapper with `overflow: hidden`
 *
 * ## `.pswp__container`
 * Container that holds the slides. PhotoSwipe keeps only 3 in the DOM to save memory. Don't modify these elements, data
 * is added later on.
 *
 * ## `.pswp__ui`
 * Root element of non-core PhotoSwipe UI, i.e. the part of the DOM managed by us. All the chrome of the lightbox lives
 * here, while PhotoSwipe manages the slides themselves.
 *
 * ## `.pswp__preloader`
 * Loading spinner - demo: https://codepen.io/dimsemenov/pen/yyBWoR
 * element will get class `pswp__preloader--active` when preloader is running
 */
const DEFAULT_PHOTOSWIPEUI_INNERHTML = `
    <div class="pswp__bg"></div>

    <div class="pswp__scroll-wrap">

        <div class="pswp__container">
                <div class="pswp__item"></div>
                <div class="pswp__item"></div>
                <div class="pswp__item"></div>
        </div>

        <div class="pswp__ui pswp__ui--hidden">
            <div class="pswp__top-bar">

                <div class="pswp__creator">
                    <div class="pswp__creator__avatar">
                        <img data-pswp-avatar-image src=""></img>
                    </div>
                    <div data-pswp-creator-name class="pswp__creator__name"></div>
                </div>

                <div class="pswp__navigation">
                    <div class="pswp__counter" style="display:none"></div>
                </div>

                <button class="pswp__button pswp__button--download" title="Download" type="button">
                    ${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faArrowToBottom} />)}
                </button>
                <button class="pswp__button pswp__button--close" title="Close (Esc)" type="button">
                    ${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faTimes} />)}
                </button>

                <div class="pswp__preloader">
                    <div class="pswp__preloader__icn">
                        <div class="pswp__preloader__cut">
                            <div class="pswp__preloader__donut"></div>
                        </div>
                    </div>
                </div>
            </div>

            <button class="pswp__button pswp__button--arrow--left pswp__button--arrow" title="Previous (arrow left)">
                ${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faAngleLeft} />)}
            </button>

            <button class="pswp__button pswp__button--arrow--right pswp__button--arrow" title="Next (arrow right)">
                ${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faAngleRight} />)}
            </button>

            <div class="pswp__caption">
                <div class="pswp__caption__center"></div>
            </div>

        </div>

    </div>
`;

export const PHOTOSWIPEUI_INNERHTML_NO_CREATOR = `
    <div class="pswp__bg"></div>

    <div class="pswp__scroll-wrap">

        <div class="pswp__container">
                <div class="pswp__item"></div>
                <div class="pswp__item"></div>
                <div class="pswp__item"></div>
        </div>

        <div class="pswp__ui pswp__ui--hidden">
            <div class="pswp__top-bar">

                <div class="pswp__navigation">
                    <div class="pswp__counter" style="display:none"></div>
                </div>

                <button class="pswp__button pswp__button--download" title="Download" type="button">
                    ${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faArrowToBottom} />)}
                </button>
                <button class="pswp__button pswp__button--close" title="Close (Esc)" type="button">
                    ${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faTimes} />)}
                </button>

                <div class="pswp__preloader">
                    <div class="pswp__preloader__icn">
                        <div class="pswp__preloader__cut">
                            <div class="pswp__preloader__donut"></div>
                        </div>
                    </div>
                </div>
            </div>

            <button class="pswp__button pswp__button--arrow--left pswp__button--arrow" title="Previous (arrow left)">
                ${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faAngleLeft} />)}
            </button>

            <button class="pswp__button pswp__button--arrow--right pswp__button--arrow" title="Next (arrow right)">
                ${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faAngleRight} />)}
            </button>

            <div class="pswp__caption">
                <div class="pswp__caption__center"></div>
            </div>

        </div>

    </div>
`;

/** Fallback function in case, for whatever reason, we can't get real bounds for an attachment thumbnail */
export function defaultThumbBounds(): ReturnType<NonNullable<PhotoSwipe.Options["getThumbBoundsFn"]>> {
	return {
		x: window.innerWidth / 2,
		y: window.innerHeight / 2,
		w: 0,
	};
}

export const openLightbox = async ({
	items,
	initialIndex,
	photoSwipeUIInnerHTML,
	onDestroy,
	onDelete,
	addCaptionHTMLFn,
	getThumbBoundsFn,
	afterChange,
}: LightboxProps): Promise<
	| {
			pswp: PhotoSwipe<PSUIOptions>;
			rootElement: HTMLDivElement;
	  }
	| undefined
> => {
	if (items.length === 0) return;
	let rootElement = document.createElement("div");
	rootElement.className = "pswp";
	rootElement.tabIndex = -1;
	rootElement.setAttribute("role", "dialog");
	rootElement.setAttribute("aria-hidden", "true");
	rootElement.innerHTML = photoSwipeUIInnerHTML ?? DEFAULT_PHOTOSWIPEUI_INNERHTML;

	document.body.appendChild(rootElement);

	let pswp = new PhotoSwipe(rootElement, PhotoSwipeUI, items, {
		index: initialIndex,
		showHideOpacity: true,
		history: false, // might be nice to have, but hash changes can trigger CollapsiblePanel closing
		addCaptionHTMLFn,
		getThumbBoundsFn: getThumbBoundsFn ?? defaultThumbBounds,
		onDelete,
	});

	typeof onDestroy === "function" && pswp.listen("destroy", onDestroy);
	// the original afterChange takes no parameters which makes it not so useful in some cases
	// (e.g. make some change inside PhotoSwipper without directly exposing the 'pswp' instance)
	// eslint-disable-next-line no-underscore-dangle
	const _afterChange = () => afterChange?.(pswp);
	typeof afterChange === "function" && pswp.listen("afterChange", _afterChange);
	pswp.init();

	return { pswp, rootElement };
};
